import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import dayjs from 'dayjs';
import { Modal, Form, Select, DatePicker, InputNumber, Input, Button, Segmented, message } from '../../ui';
import { ArrowDownLeft, ArrowUpRight, Wallet } from 'lucide-react';
import { request } from '../../api/client';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { bankAcctOption } from './PaymentFormModal';
import { inr2 } from '../../lib/format';
import { disableFuture } from '../../lib/dateRange';

type Kind = 'WITHDRAWAL' | 'DEPOSIT';

const INSTRUMENTS: Record<Kind, { value: string; label: string }[]> = {
  WITHDRAWAL: [
    { value: 'ATM', label: 'ATM' },
    { value: 'CHEQUE', label: 'Self cheque' },
    { value: 'SLIP', label: 'Counter withdrawal slip' },
    { value: 'TRANSFER', label: 'Transfer' },
    { value: 'OTHER', label: 'Other' },
  ],
  DEPOSIT: [
    { value: 'SLIP', label: 'Deposit slip' },
    { value: 'CHEQUE', label: 'Cheque' },
    { value: 'TRANSFER', label: 'Transfer' },
    { value: 'ATM', label: 'Cash deposit machine' },
    { value: 'OTHER', label: 'Other' },
  ],
};

/**
 * Record the company's own money moving between a bank account and cash.
 *
 * The question this form exists to ask is "where did the cash GO" — cash drawn at the counter is
 * routinely a personal drawing or an expense settled on the spot, and only the money actually kept as
 * office float belongs in the cash book. The purpose answers that; the remark is mandatory because a
 * contra entry has no invoice, no retailer and no claim behind it, so what the recorder types is the
 * only explanation this movement will ever carry.
 */
export function CashMovementModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const [kind, setKind] = useState<Kind>('WITHDRAWAL');

  const { data: bankRaw } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
    enabled: open,
  });
  const bankOpts = (Array.isArray(bankRaw) ? bankRaw : ((bankRaw as any)?.data ?? []));

  const { data: purposeRaw } = useQuery({
    queryKey: ['cash-movement-purposes'],
    queryFn: () => request<any>({ url: '/cash-movements/purposes' }),
    enabled: open,
  });
  const purposes: { value: string; label: string; affectsCashBook: boolean }[] =
    (purposeRaw as any)?.[kind] ?? (purposeRaw as any)?.data?.[kind] ?? [];

  // What the cash book holds right now — shown so the person recording a deposit can see whether the
  // float actually covers it before they commit.
  const { data: cihRaw } = useQuery({
    queryKey: ['cash-in-hand'],
    queryFn: () => request<any>({ url: '/cash-movements/cash-in-hand' }),
    enabled: open,
  });
  const cashInHand = Number((cihRaw as any)?.cashInHand ?? (cihRaw as any)?.data?.cashInHand ?? 0);

  // Direction resets with the dialog; the FIELDS are seeded by `initialValues` + the form's `key`
  // below. Seeding them from an effect instead races the portal's mount and lands on a form that is
  // about to be replaced — which is exactly how the date, purpose and instrument arrived empty.
  useEffect(() => { if (open) setKind('WITHDRAWAL'); }, [open]);

  const purpose = Form.useWatch('purpose', form);
  const amount = Number(Form.useWatch('amount', form) ?? 0);
  // Falls back to the default purpose's own answer while the catalogue is still loading, so the line
  // under the picker never claims the opposite of what will be saved.
  const affectsCashBook = purposes.find((p) => p.value === purpose)?.affectsCashBook ?? (purpose === 'CASH_IN_HAND');
  // Only a cash-book deposit can overdraw the float: a drawing or an outside infusion never touches it.
  const overdrawsFloat = kind === 'DEPOSIT' && affectsCashBook && amount > 0 && amount > cashInHand;

  const save = useConfirmedMutation({ title: "Record this cash movement?", content: "It posts to the cash book immediately. Correcting it later needs a reversing entry.", okText: "Yes, record" }, {
    mutationFn: (vals: any) => request<any>({
      url: '/cash-movements', method: 'POST',
      data: {
        kind,
        bankAccountId: vals.bankAccountId,
        amount: Number(vals.amount),
        txnDate: dayjs(vals.txnDate).format('YYYY-MM-DD'),
        purpose: vals.purpose,
        remark: vals.remark,
        instrument: vals.instrument,
        referenceNo: vals.referenceNo,
        attachmentUrls: vals.attachmentUrls,
      },
    }),
    onSuccess: (r: any) => {
      const d = r?.data ?? r;
      message.success(`${d.txnNo} recorded — ${kind === 'WITHDRAWAL' ? 'cash withdrawn' : 'cash deposited'}`);
      if (d.cashInHandNegative) {
        message.warning('Cash in hand is now negative — a cash collection has probably not been recorded yet.');
      }
      qc.invalidateQueries({ queryKey: ['payments-bank-summary'] });
      qc.invalidateQueries({ queryKey: ['bank-register'] });
      qc.invalidateQueries({ queryKey: ['cash-in-hand'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to record the movement'),
  });

  const isWithdrawal = kind === 'WITHDRAWAL';
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} width={640}
      title="Record a cash movement"
      description="Your own money moving between a bank account and cash. It posts to no retailer ledger and no staff wallet.">
      <div className="mb-4">
        <Segmented
          value={kind}
          onChange={(v) => {
            const next = v as Kind;
            setKind(next);
            // The purpose lists differ per direction, so a carried-over value would be invalid.
            form.setFieldsValue({ purpose: 'CASH_IN_HAND', instrument: next === 'WITHDRAWAL' ? 'ATM' : 'SLIP' });
          }}
          options={[
            { value: 'WITHDRAWAL', label: <span className="inline-flex items-center gap-1.5"><ArrowUpRight size={14} />Withdraw cash from bank</span> },
            { value: 'DEPOSIT', label: <span className="inline-flex items-center gap-1.5"><ArrowDownLeft size={14} />Deposit cash into bank</span> },
          ]}
        />
      </div>

      <Form
        form={form}
        // `key` remounts the form on each open so `initialValues` is applied fresh — the seed pattern
        // this kit needs (see the Form docs): initialValues + key, never setFieldsValue from an effect.
        key={open ? 'open' : 'closed'}
        layout="vertical"
        initialValues={{ txnDate: dayjs(), purpose: 'CASH_IN_HAND', instrument: 'ATM' }}
        onFinish={(v) => save.mutate(v)}
      >
        <Form.Item name="bankAccountId" label={isWithdrawal ? 'Withdrawn from' : 'Deposited into'} rules={[{ required: true, message: 'Choose the bank account' }]}>
          <Select showSearch optionFilterProp="searchLabel"
            placeholder={bankOpts.length ? 'Select a company bank account' : 'No bank accounts configured'}
            disabled={!bankOpts.length} options={bankOpts.map(bankAcctOption)} />
        </Form.Item>

        <div className="flex flex-wrap gap-4 [&>*]:min-w-[150px] [&>*]:flex-1">
          <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true, message: 'Enter the amount' }]}>
            <InputNumber className="w-full" min={1} step={500} />
          </Form.Item>
          <Form.Item name="txnDate" label="Date the money moved" rules={[{ required: true, message: 'Pick the date' }]}>
            <DatePicker className="w-full" disabledDate={disableFuture} />
          </Form.Item>
        </div>

        <Form.Item
          name="purpose"
          label={isWithdrawal ? 'Where did the cash go?' : 'Where did the cash come from?'}
          rules={[{ required: true, message: 'Choose what this was for' }]}
        >
          <Select options={purposes.map((p) => ({ value: p.value, label: p.label }))} />
        </Form.Item>

        {/* The consequence of the purpose, said plainly — this is the difference between a float the
            cash book can account for and money that has already left the business. */}
        <div className="-mt-2 mb-4 flex items-start gap-2 rounded-control bg-surface-2 px-3 py-2 type-caption text-muted">
          <Wallet size={14} className="mt-0.5 shrink-0" />
          <span>
            {affectsCashBook
              ? isWithdrawal
                ? <>This <b className="text-content">adds to cash in hand</b> — the company still holds this money, as cash.</>
                : <>This <b className="text-content">reduces cash in hand</b> — the float goes into the bank.</>
              : <>This does <b className="text-content">not</b> touch cash in hand — the money leaves (or enters) the business here, it is not held as float.</>}
            {' '}Cash in hand today: <b className="text-content">{inr2(cashInHand)}</b>.
          </span>
        </div>

        {overdrawsFloat && (
          <div className="mb-4 rounded-control border border-warning-500/40 bg-warning-500/10 px-3 py-2 type-caption text-warning-600 dark:text-harvest-300">
            This deposit is larger than the recorded cash in hand ({inr2(cashInHand)}). It will still be saved — that
            usually means a cash collection has not been entered yet — but check the collections first.
          </div>
        )}

        <div className="flex flex-wrap gap-4 [&>*]:min-w-[150px] [&>*]:flex-1">
          <Form.Item name="instrument" label="How">
            <Select options={INSTRUMENTS[kind]} />
          </Form.Item>
          <Form.Item name="referenceNo" label="Reference" tooltip="Cheque no, deposit slip no or ATM reference — what ties this to the transaction on the bank statement.">
            <Input placeholder="e.g. slip 88213" />
          </Form.Item>
        </div>

        <Form.Item
          name="remark"
          label="Remark"
          rules={[{ required: true, message: 'Say why this money moved' }, { min: 3, message: 'Write a few words' }]}
          extra="Required. There is no invoice or claim behind a cash movement — this note is the whole explanation. Name the person if someone took the cash."
        >
          <Input.TextArea rows={2} maxLength={500} placeholder={isWithdrawal ? 'e.g. Drawn for the Nagpur camp expenses, taken by Vikram' : 'e.g. Banked the collections of 02–03 Sep'} />
        </Form.Item>

        <Form.Item name="attachmentUrls" label={isWithdrawal ? 'Slip / receipt (optional)' : 'Deposit slip (optional)'}>
          <MultiFileUploadField category="receipt" buttonLabel="Upload slip" />
        </Form.Item>

        <div className="mt-2 flex flex-wrap justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" loading={save.isPending}>
            {isWithdrawal ? 'Record withdrawal' : 'Record deposit'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
